import { AGENT_SESSION_FRESHNESS_MS, SavedTabsState, ThreadTab } from './agent.types';

// Reading the bottom-bar tabs back from localStorage. The saved value outlives every deploy and
// is read on every page load, so anything malformed is dropped here rather than trusted: a tab
// the shell cannot render would break it on every load until the user cleared site data.

function isThreadTab(value: unknown): value is ThreadTab {

  if (typeof value !== 'object' || value === null) return false;
  const tab = value as Partial<ThreadTab>;
  return typeof tab.threadId === 'string' && tab.threadId.length > 0;
}

/**
 * Parses the saved tabs. Keeps only tabs with a thread id (one per thread, newest `maxTabs`), and
 * keeps the active tab only when it is one of those tabs or the new-chat tab. A value without a
 * timestamp, or older than the session-freshness window, restores nothing.
 */
export function parseSavedTabs(raw: string | null, now: number, maxTabs: number, newChatTabId: string): SavedTabsState {

  const empty: SavedTabsState = { tabs: [], activeTabId: null, ts: now };
  if (!raw) return empty;
  let parsed: Partial<SavedTabsState>;
  try {
    parsed = JSON.parse(raw) as Partial<SavedTabsState>;
  } catch {
    return empty;
  }
  if (typeof parsed !== 'object' || parsed === null) return empty;
  if (typeof parsed.ts !== 'number' || now - parsed.ts > AGENT_SESSION_FRESHNESS_MS) return empty;

  const seen = new Set<string>();
  const tabs: ThreadTab[] = [];
  for (const tab of Array.isArray(parsed.tabs) ? parsed.tabs : []) {
    if (!isThreadTab(tab) || seen.has(tab.threadId)) continue;
    seen.add(tab.threadId);
    tabs.push({ threadId: tab.threadId, title: typeof tab.title === 'string' ? tab.title : '' });
  }
  const kept = tabs.slice(-maxTabs);

  const active = parsed.activeTabId;
  const activeIsKnown = active === newChatTabId || kept.some(tab => tab.threadId === active);
  return {
    tabs: kept,
    activeTabId: typeof active === 'string' && activeIsKnown ? active : null,
    ts: parsed.ts,
  };
}
